import { useState, useCallback, useRef, useEffect } from 'react'
import { Upload, Loader2, X, FolderUp, FileText } from 'lucide-react'
import { createTarArchive } from '../lib/tar'
import { createImportFromUpload } from '../lib/api'
import { HelpLink } from './HelpLink'

// =============================================================================
// Constants
// =============================================================================

const MAX_TOTAL_SIZE = 100 * 1024 * 1024 // 100MB
const MAX_FILE_COUNT = 5000
const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB per file

const SKIP_DIRS = new Set([
  '.git',
  'node_modules',
  '__pycache__',
  '.venv',
  'venv',
  '.env',
  'dist',
  'build',
  '.next',
  '.nuxt',
  '.DS_Store',
])

const SKIP_FILES = new Set(['.DS_Store', 'Thumbs.db'])

const SKIP_EXTENSIONS = new Set(['.pyc'])

// =============================================================================
// Types
// =============================================================================

interface FolderDropZoneProps {
  onImported: (appName: string, importId: number) => void
  onCancel: () => void
}

type Phase = 'idle' | 'reading' | 'uploading' | 'redirecting' | 'error'

interface FileEntry {
  path: string
  file: File
}

// =============================================================================
// Helpers
// =============================================================================

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.substring(dot) : ''
}

function isHtmlFileName(name: string): boolean {
  const ext = getExtension(name).toLowerCase()
  return ext === '.html' || ext === '.htm'
}

// =============================================================================
// Recursive directory reader
// =============================================================================

function readEntries(
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    reader.readEntries(resolve, reject)
  })
}

function fileFromEntry(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject)
  })
}

async function readDirectoryEntries(
  dirEntry: FileSystemDirectoryEntry,
  basePath: string = '',
): Promise<{ files: FileEntry[]; filtered: number }> {
  const files: FileEntry[] = []
  let filtered = 0

  const reader = dirEntry.createReader()
  let batch: FileSystemEntry[] = []

  // readEntries returns at most 100 items per call -- loop until empty
  do {
    batch = await readEntries(reader)
    for (const entry of batch) {
      // Skip directories/files by name
      if (SKIP_DIRS.has(entry.name) || SKIP_FILES.has(entry.name)) {
        filtered++
        continue
      }

      const entryPath = basePath ? `${basePath}/${entry.name}` : entry.name

      if (entry.isFile) {
        // Skip by extension
        if (SKIP_EXTENSIONS.has(getExtension(entry.name))) {
          filtered++
          continue
        }

        const file = await fileFromEntry(entry as FileSystemFileEntry)

        // Skip oversized files
        if (file.size > MAX_FILE_SIZE) {
          filtered++
          continue
        }

        files.push({ path: entryPath, file })
      } else if (entry.isDirectory) {
        const sub = await readDirectoryEntries(
          entry as FileSystemDirectoryEntry,
          entryPath,
        )
        files.push(...sub.files)
        filtered += sub.filtered
      }
    }
  } while (batch.length > 0)

  return { files, filtered }
}

// =============================================================================
// Component
// =============================================================================

export default function FolderDropZone({
  onImported,
  onCancel,
}: FolderDropZoneProps) {
  const [dragActive, setDragActive] = useState(false)
  const [phase, setPhase] = useState<Phase>('idle')
  const [files, setFiles] = useState<FileEntry[]>([])
  const [folderName, setFolderName] = useState('')
  const [totalSize, setTotalSize] = useState(0)
  const [filteredCount, setFilteredCount] = useState(0)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const redirectingRef = useRef(false)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const processImportSelection = useCallback(
    async (
      selectedSourceName: string,
      readFiles: () => Promise<{ files: FileEntry[]; filtered: number }>,
    ) => {
      setFolderName(selectedSourceName)
      setPhase('reading')

      try {
        const result = await readFiles()

        // Validate total size
        const size = result.files.reduce((sum, f) => sum + f.file.size, 0)
        if (size > MAX_TOTAL_SIZE) {
          setError(
            `Total size ${formatSize(size)} exceeds the ${formatSize(MAX_TOTAL_SIZE)} limit`,
          )
          setPhase('error')
          return
        }

        // Validate file count
        if (result.files.length > MAX_FILE_COUNT) {
          setError(
            `Too many files (${result.files.length}). Maximum is ${MAX_FILE_COUNT}.`,
          )
          setPhase('error')
          return
        }

        if (result.files.length === 0) {
          setError('No supported files found in the selected folder or file')
          setPhase('error')
          return
        }

        setFiles(result.files)
        setTotalSize(size)
        setFilteredCount(result.filtered)
        setPhase('uploading')
        setUploadProgress(0)

        // Create tar archive
        const archive = await createTarArchive(result.files)

        // Upload via XHR
        const uploadResult = await createImportFromUpload(
          archive,
          selectedSourceName,
          undefined,
          undefined,
          (pct) => setUploadProgress(pct),
        )
        if (redirectingRef.current) return
        redirectingRef.current = true
        setPhase('redirecting')
        onImported(uploadResult.app_name, uploadResult.import_id)
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Upload failed unexpectedly',
        )
        setPhase('error')
      }
    },
    [onImported],
  )

  const openFolderPicker = useCallback(() => {
    folderInputRef.current?.click()
  }, [])

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFolderPicked = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files
      if (!selectedFiles || selectedFiles.length === 0) return

      setError(null)

      const files: FileEntry[] = []
      let filtered = 0
      let pickedFolderName = ''

      for (const file of Array.from(selectedFiles)) {
        const relativePath = file.webkitRelativePath || file.name
        const pathParts = relativePath.split('/').filter(Boolean)
        if (pathParts.length > 1 && !pickedFolderName) {
          pickedFolderName = pathParts[0]
        }

        const filePathParts = pathParts.length > 1 ? pathParts.slice(1) : pathParts
        if (filePathParts.length === 0) {
          filtered++
          continue
        }

        const fileName = filePathParts[filePathParts.length - 1]
        const directoryParts = filePathParts.slice(0, -1)

        if (
          directoryParts.some((part) => SKIP_DIRS.has(part)) ||
          SKIP_FILES.has(fileName) ||
          SKIP_EXTENSIONS.has(getExtension(fileName)) ||
          file.size > MAX_FILE_SIZE
        ) {
          filtered++
          continue
        }

        files.push({ path: filePathParts.join('/'), file })
      }

      const finalFolderName = pickedFolderName || 'uploaded-project'
      await processImportSelection(finalFolderName, async () => ({ files, filtered }))
      e.target.value = ''
    },
    [processImportSelection],
  )

  const handleFilePicked = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files
      if (!selectedFiles || selectedFiles.length === 0) return

      setError(null)

      if (selectedFiles.length !== 1) {
        setError('Please choose a single HTML file or use a folder upload')
        setPhase('error')
        e.target.value = ''
        return
      }

      const singleFile = selectedFiles[0]
      if (!isHtmlFileName(singleFile.name)) {
        setError('Single-file upload currently supports only .html or .htm files')
        setPhase('error')
        e.target.value = ''
        return
      }
      if (singleFile.size > MAX_FILE_SIZE) {
        setError(
          `File size ${formatSize(singleFile.size)} exceeds the ${formatSize(MAX_FILE_SIZE)} limit`,
        )
        setPhase('error')
        e.target.value = ''
        return
      }

      const baseName = singleFile.name.replace(/\.[^.]+$/, '')
      const selectedName = baseName || 'static-html'
      await processImportSelection(
        selectedName,
        async () => ({ files: [{ path: singleFile.name, file: singleFile }], filtered: 0 }),
      )
      e.target.value = ''
    },
    [processImportSelection],
  )

  useEffect(() => {
    const input = folderInputRef.current
    if (input) {
      input.setAttribute('webkitdirectory', '')
      input.setAttribute('directory', '')
    }

    return () => {
      redirectingRef.current = false
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(true)
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
  }, [])

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setDragActive(false)
      setError(null)

      // Find the first directory entry
      const items = e.dataTransfer.items
      let dirEntry: FileSystemDirectoryEntry | null = null

      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry()
        if (entry?.isDirectory) {
          dirEntry = entry as FileSystemDirectoryEntry
          break
        }
      }

      if (dirEntry) {
        await processImportSelection(
          dirEntry.name,
          async () => readDirectoryEntries(dirEntry),
        )
        return
      }

      const droppedFiles = e.dataTransfer.files
      if (!droppedFiles || droppedFiles.length === 0) {
        setError('Please drop a folder or a single HTML file')
        setPhase('error')
        return
      }
      if (droppedFiles.length !== 1) {
        setError('Please drop one HTML file or a full folder')
        setPhase('error')
        return
      }

      const singleFile = droppedFiles[0]
      if (!isHtmlFileName(singleFile.name)) {
        setError('Single-file drag & drop supports only .html or .htm files')
        setPhase('error')
        return
      }
      if (singleFile.size > MAX_FILE_SIZE) {
        setError(
          `File size ${formatSize(singleFile.size)} exceeds the ${formatSize(MAX_FILE_SIZE)} limit`,
        )
        setPhase('error')
        return
      }

      const baseName = singleFile.name.replace(/\.[^.]+$/, '')
      const selectedName = baseName || 'static-html'
      await processImportSelection(
        selectedName,
        async () => ({ files: [{ path: singleFile.name, file: singleFile }], filtered: 0 }),
      )
    },
    [processImportSelection],
  )

  function handleRetry() {
    setPhase('idle')
    setError(null)
    setFiles([])
    setFolderName('')
    setTotalSize(0)
    setFilteredCount(0)
    setUploadProgress(0)
    redirectingRef.current = false
  }

  // =========================================================================
  // Render
  // =========================================================================

  // Idle: drop zone
  if (phase === 'idle') {
    return (
      <div className="space-y-4">
        <input
          ref={folderInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFolderPicked}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".html,.htm,text/html"
          className="hidden"
          onChange={handleFilePicked}
        />
        <div
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`h-48 flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed transition-colors cursor-default ${
            dragActive
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50'
          }`}
        >
          <FolderUp
            className={`w-10 h-10 ${dragActive ? 'text-primary' : 'text-muted-foreground'}`}
          />
          <div className="text-center">
            <p className="text-sm font-medium">
              Drag & drop a project folder or one HTML file
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              or choose a folder/file using the pickers below
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              node_modules, .git, and other common directories are automatically
              filtered
            </p>
          </div>
        </div>
        <div className="flex flex-wrap justify-between gap-3">
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={openFolderPicker}
              className="px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors inline-flex items-center gap-2"
            >
              <FolderUp className="w-4 h-4" />
              Choose folder
            </button>
            <button
              type="button"
              onClick={openFilePicker}
              className="px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors inline-flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Choose HTML file
            </button>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  // Reading: spinner
  if (phase === 'reading') {
    return (
      <div className="py-8 text-center space-y-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
        <p className="text-sm font-medium">Reading files...</p>
        {folderName && (
          <p className="text-xs text-muted-foreground">
            Scanning {folderName}
          </p>
        )}
      </div>
    )
  }

  // Uploading: progress bar
  if (phase === 'uploading') {
    return (
      <div className="py-8 text-center space-y-4">
        <Upload className="w-8 h-8 text-primary mx-auto" />
        <p className="text-sm font-medium">
          Uploading {files.length} files ({formatSize(totalSize)})...
        </p>
        <div className="w-full max-w-xs mx-auto">
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {uploadProgress}%
          </p>
        </div>
        {filteredCount > 0 && (
          <p className="text-xs text-muted-foreground">
            {filteredCount} file{filteredCount !== 1 ? 's' : ''}/director
            {filteredCount !== 1 ? 'ies' : 'y'} filtered out
          </p>
        )}
      </div>
    )
  }

  // Redirecting: handoff to workspace
  if (phase === 'redirecting') {
    return (
      <div className="py-8 text-center space-y-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
        <p className="text-sm font-medium">Opening workspace...</p>
        <p className="text-xs text-muted-foreground">
          Import continues in the workspace terminal
        </p>
      </div>
    )
  }

  // Error: retry
  return (
    <div className="py-8 text-center space-y-4">
      <div className="w-10 h-10 rounded-full bg-destructive/20 flex items-center justify-center mx-auto">
        <X className="w-6 h-6 text-destructive" />
      </div>
      <p className="text-lg font-medium text-destructive">Upload failed</p>
      {error && (
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          {error}
        </p>
      )}
      <HelpLink section="import">View import guide</HelpLink>
      <div className="flex gap-3 pt-2 max-w-xs mx-auto">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleRetry}
          className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  )
}
